import { DashboardProvider, useDashboard } from "@/context/dashboard";
import Dashboard from "@/Layouts/Dashboard";
import { decryptInteger } from "@/Utils/encryptInteger";
import { Skeleton } from "@mui/material";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { FaTicketAlt } from "react-icons/fa";
import { formatMonthDayYear } from "@/Utils/dateFormat";
import MetaTags from "@/Components/SEO/MetaTags";

export default function IndexPage() {
  const router = useRouter();
  useEffect(() => {
    let encryptedData = localStorage.getItem("user_data");
    if (!encryptedData) {
      router.push("/");
    }
  }, []);

  return (
    <DashboardProvider>
      <MetaTags 
        title="Dashboard - Qoncert"
        description="View and manage your concert tickets, track upcoming shows, and access your Qoncert account dashboard."
        keywords="dashboard, my tickets, concert tickets, upcoming shows, ticket management, account dashboard"
        canonicalUrl={"/dashboard"}
      />
      <DashboardContent />
    </DashboardProvider>
  );
}

function DashboardContent() {
  const router = useRouter();
  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const { tickets, setCurrentPage, currentPage, dsQuery } = useDashboard();

  useEffect(() => {
    if (tickets && tickets.length > 0) {
      setFilteredData(tickets);
    }
  }, [tickets]);

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const div = scrollRef.current;
      const scrollTop = div.scrollTop;
      const scrollHeight = div.scrollHeight - div.clientHeight;
      const scrollPercent = (scrollTop / scrollHeight) * 100;
      if (tickets?.length >= (currentPage + 1) * 10 && scrollPercent >= 100) {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    }
  }, [currentPage, tickets, setCurrentPage]);

  useEffect(() => {
    setLoading(true);
    const query = dsQuery.toLowerCase();
    async function fetchMyTickets() {
      try {
        const encryptedData = localStorage.getItem("user_data");
        let data = JSON.parse(decryptInteger(encryptedData));
        let web_token = data?.web_token;

        const formData = new URLSearchParams();
        formData.append("action", "fetchMyTickets_v3");
        formData.append("page_index", 0);
        formData.append("search_text", query);

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageTicket`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${web_token}` },
            body: formData,
          }
        );

        let resData = await response.json();

        setTimeout(() => {
          setFilteredData(resData.data);
          setLoading(false);
        }, 600);
      } catch (error) {
        console.log("error", error);
      }
    }

    fetchMyTickets();
  }, [dsQuery, tickets]);

  useEffect(() => {
    const div = scrollRef.current;
    if (div) {
      div.addEventListener("scroll", handleScroll);
      return () => div.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  return (
    <Dashboard>
      <Head>
        <title>Qoncert App | Dashboard</title>
      </Head>
      <div
        ref={scrollRef}
        id="dashboard-container"
        className="custom_scrll_bar  pt-5 lg:pt-5 px-6 lg:px-10 pb-4"
      >
        <h2 className="dashboard-heading pl-3 lg:pl-0 mt-0">Tickets</h2>
        <div className="mt-6 lg:mt-11">
          <div className="w-fit mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 lg:gap-[90px]">
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="gradient-skeleton w-[327px] lg:w-[262px] h-[220px] lg:h-[365px]"
                  >
                    <Skeleton
                      variant="circle"
                      width="100%"
                      height="100%"
                      sx={{ margin: "0 auto", borderRadius: "16px" }}
                    />
                  </div>
                ))
              ) : filteredData && filteredData.length > 0 ? (
                filteredData.map((ticket, i) => {
                  const showDate = ticket.show?.show_date;
                  const showTime = ticket.show?.show_time;

                  let finalDate = "";
                  
                  finalDate = formatMonthDayYear(showDate);
                  
                  const [hours, minutes] = showTime.split(":").map(Number);
                  const amPm = hours >= 12 ? "PM" : "AM";
                  const convertedTime = `${hours % 12 || 12}:${minutes
                    .toString()
                    .padStart(2, "0")} ${amPm}`;

                  finalDate = `${finalDate} | ${convertedTime}`;

                  return (
                    <div key={i + 1} className="h-full">
                      <div
                        onClick={() =>
                          router.push(`/tickets/${ticket.booking_id}`)
                        }
                        className="ticket-card rounded-[28px] lg:rounded-[18px] px-5 py-3 lg:px-10 lg:pt-7 lg:pb-4 w-full lg:max-w-[262px]"
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-4 items-center lg:gap-0 sm:flex-col">
                            <div className="ticket-image min-w-[120px] aspect-square lg:aspect-auto lg:w-full lg:max-w-[222px]">
                              <img
                                src={ticket?.show?.banner}
                                className="h-[120px] w-[120px] lg:w-full lg:h-full object-cover object-center rounded-lg"
                                alt=""
                                loading="eager"
                              />
                            </div>
                            <div className="w-full overflow-hidden font-urbanist text-left gap-[14px] lg:gap-0 py-4 lg:py-0 lg:text-center flex flex-col">
                              <p className="show-name truncate mt-2 text-[20px] lg:text-base font-[700] font-urbanist line-clamp-2">
                                {ticket?.show?.name}
                              </p>
                              <p className="show-dates md:mt-[2px] text-[14px] font-[400] text-[#d2d3d4] font-urbanist">
                                {finalDate}
                              </p>
                              <p className="text-[#E0E0E0] md:hidden font-urbanist text-xs">
                                {ticket?.show?.venue?.city},{" "}
                                {ticket?.show?.venue?.state}
                              </p>
                            </div>
                          </div>
                          <div className="md:hidden py-[2px] border-t border-[#35383F]"></div>
                          <button
                            className={`
                                ${
                                  ticket?.status === 2 ||
                                  ticket?.status === 5 ||
                                  ticket?.status === 3
                                    ? "expired-button"
                                    : ""
                                } 
                                ${
                                  ticket?.status === 4
                                    ? "used-ticket"
                                    : ""
                                }  
                                text-[14px] md:mt-3 lg:max-w-[175px] mx-auto w-full lg:text-base rounded-[12px] md:rounded-[10px]
                            `}
                          >
                            <p>
                              {ticket?.status === 2
                                ? "Refunded"
                                : ticket?.status === 5
                                ? "Expired"
                                : ticket?.status === 4
                                ? "Ticket Used"
                                : ticket?.status === 3
                                ? "Transferred"
                                : "View Ticket"}
                            </p>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center h-full min-h-[calc(100vh-500px)] flex flex-col justify-end col-span-3 py-10">
                  <FaTicketAlt size={48} className="text-white mx-auto mb-4" />
                  <p className="text-lg font-semibold text-white">
                    No tickets found
                  </p>
                  <button
                    onClick={() => router.push("/shows")}
                    className="mt-4 px-6 font-extrabold py-2 main_btn_bg text-white rounded-lg transition"
                  >
                    Find Our Latest Shows
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
}
